import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { adminAPI } from '../../services/api';

function ReviewSubmissions() {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reviewingId, setReviewingId] = useState(null);
    const [reviewNotes, setReviewNotes] = useState('');
    const [uploadingCert, setUploadingCert] = useState(null);

    const fetchSubmissions = useCallback(async () => {
        try {
            const response = await adminAPI.getSubmissions();
            setSubmissions(response.data);
        } catch (error) {
            toast.error('Failed to load submissions');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSubmissions();
    }, [fetchSubmissions]);

    const handleReview = async (submissionId, status) => {
        setReviewingId(submissionId);
        try {
            await adminAPI.reviewSubmission(submissionId, status, reviewNotes);
            toast.success(`Submission ${status}!`);
            setReviewNotes('');
            fetchSubmissions();
        } catch (error) {
            toast.error('Failed to review submission');
        } finally {
            setReviewingId(null);
        }
    };

    const handleCertificateUpload = async (e, studentId, projectId) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingCert(`${studentId}-${projectId}`);
        const formData = new FormData();
        formData.append('certificate', file);

        try {
            await adminAPI.uploadCertificate(studentId, projectId, formData);
            toast.success('Certificate uploaded successfully!');
        } catch (error) {
            toast.error('Failed to upload certificate');
        } finally {
            setUploadingCert(null);
        }
    };

    if (loading) {
        return <div className="loading">Loading submissions...</div>;
    }

    const pendingSubmissions = submissions.filter((s) => s.status === 'pending');
    const reviewedSubmissions = submissions.filter((s) => s.status !== 'pending');

    return (
        <div className="component-container">
            <h2>Review Submissions</h2>

            <div className="submissions-section">
                <h3>Pending Submissions ({pendingSubmissions.length})</h3>
                <div className="submissions-list">
                    {pendingSubmissions.length === 0 ? (
                        <p className="empty-message">No pending submissions</p>
                    ) : (
                        pendingSubmissions.map((submission) => (
                            <div key={submission._id} className="submission-card pending">
                                <div className="submission-header">
                                    <div>
                                        <h4>{submission.student?.name}</h4>
                                        <p className="email">{submission.student?.email}</p>
                                    </div>
                                    <span className="badge badge-pending">{submission.status}</span>
                                </div>

                                <div className="submission-details">
                                    <p><strong>Project:</strong> {submission.project?.name}</p>
                                    <p><strong>Category:</strong> {submission.project?.category}</p>
                                    <p><strong>Type:</strong> {submission.project?.type}</p>
                                    <p>
                                        <strong>GitHub Link:</strong>{' '}
                                        <a href={submission.githubLink} target="_blank" rel="noopener noreferrer">
                                            {submission.githubLink}
                                        </a>
                                    </p>
                                    <p className="submitted-date">
                                        Submitted: {new Date(submission.submittedAt).toLocaleString()}
                                    </p>
                                </div>

                                <div className="review-actions">
                                    <textarea
                                        value={reviewNotes}
                                        onChange={(e) => setReviewNotes(e.target.value)}
                                        placeholder="Add review notes (optional)"
                                        rows="2"
                                    />
                                    <div className="action-buttons">
                                        <button
                                            onClick={() => handleReview(submission._id, 'accepted')}
                                            className="btn btn-success"
                                            disabled={reviewingId === submission._id}
                                        >
                                            ✓ Accept
                                        </button>
                                        <button
                                            onClick={() => handleReview(submission._id, 'rejected')}
                                            className="btn btn-danger"
                                            disabled={reviewingId === submission._id}
                                        >
                                            ✗ Reject
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className="submissions-section">
                <h3>Reviewed Submissions ({reviewedSubmissions.length})</h3>
                <div className="submissions-list">
                    {reviewedSubmissions.length === 0 ? (
                        <p className="empty-message">No reviewed submissions</p>
                    ) : (
                        reviewedSubmissions.map((submission) => (
                            <div key={submission._id} className={`submission-card ${submission.status}`}>
                                <div className="submission-header">
                                    <div>
                                        <h4>{submission.student?.name}</h4>
                                        <p className="email">{submission.student?.email}</p>
                                    </div>
                                    <span className={`badge badge-${submission.status}`}>
                                        {submission.status}
                                    </span>
                                </div>

                                <div className="submission-details">
                                    <p><strong>Project:</strong> {submission.project?.name}</p>
                                    <p>
                                        <strong>GitHub Link:</strong>{' '}
                                        <a href={submission.githubLink} target="_blank" rel="noopener noreferrer">
                                            {submission.githubLink}
                                        </a>
                                    </p>
                                    <p className="submitted-date">
                                        Reviewed: {new Date(submission.reviewedAt).toLocaleString()}
                                    </p>
                                    {submission.reviewNotes && (
                                        <p className="review-notes">
                                            <strong>Notes:</strong> {submission.reviewNotes}
                                        </p>
                                    )}
                                </div>

                                {submission.status === 'accepted' && (
                                    <div className="certificate-upload">
                                        <h5>Upload Certificate</h5>
                                        <input
                                            type="file"
                                            id={`cert-${submission._id}`}
                                            onChange={(e) =>
                                                handleCertificateUpload(
                                                    e,
                                                    submission.student._id,
                                                    submission.project._id
                                                )
                                            }
                                            accept=".pdf,.png,.jpg,.jpeg"
                                            disabled={uploadingCert === `${submission.student._id}-${submission.project._id}`}
                                        />
                                        <label htmlFor={`cert-${submission._id}`} className="upload-label">
                                            {uploadingCert === `${submission.student._id}-${submission.project._id}`
                                                ? 'Uploading...'
                                                : '📄 Upload Certificate'}
                                        </label>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

export default ReviewSubmissions;
